import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';
import { useScheme } from '@/context/SchemeContext';
import KpiSection from './KpiSection';
import ChartsSection from './ChartsSection';
import NoSchemeSelected from '@/components/NoSchemeSelected';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { format } from 'date-fns';

const { FiAlertCircle, FiRefreshCw, FiLock, FiCalendar, FiLayers } = FiIcons;

const Dashboard = () => {
  const { selectedSchemeId, selectedScheme } = useScheme();

  const { data: bundle, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['dashboard_bundle', selectedSchemeId],
    queryFn: async () => {
      // Updated to POST because the backend now reads from php://input
      const res = await api.post('dashboard_bundle.php', { 
        scheme_id: selectedSchemeId 
      });
      return res.data;
    },
    enabled: !!selectedSchemeId,
    refetchInterval: 300000, // Refetch every 5 minutes
    retry: 1,
  });

  if (!selectedSchemeId) return <NoSchemeSelected />;

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[350px] rounded-3xl lg:col-span-1" />
          <Skeleton className="h-[350px] rounded-3xl lg:col-span-2" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
           <Skeleton className="h-[350px] rounded-3xl" />
           <Skeleton className="h-[350px] rounded-3xl" />
           <Skeleton className="h-[350px] rounded-3xl" />
        </div>
      </div>
    );
  }

  if (isError || !bundle?.status) {
    const isAuthError = error?.response?.status === 401;
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-surface-200 px-6 text-center shadow-sm">
        <div className={`w-20 h-20 ${isAuthError ? 'bg-amber-50' : 'bg-red-50'} rounded-3xl flex items-center justify-center mb-6`}>
          <SafeIcon icon={isAuthError ? FiLock : FiAlertCircle} className={`w-10 h-10 ${isAuthError ? 'text-amber-500' : 'text-red-500'}`} />
        </div>
        <h2 className="text-2xl font-extrabold text-surface-900 mb-2 tracking-tight">Data Stream Interrupted</h2>
        <p className="text-surface-500 max-w-md mb-8 leading-relaxed font-medium">
          {bundle?.message || error?.message || 'We encountered an error while fetching the dashboard statistics.'}
        </p>
        <Button onClick={() => refetch()} className="px-8 h-12 rounded-2xl min-w-[160px]">
          <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Retry Connection
        </Button>
      </div>
    );
  }

  const dashboardData = bundle.data;

  return (
    <div className="space-y-8 pb-10">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-3 text-surface-500">
          <div className="p-2.5 bg-white rounded-xl border border-surface-200 shadow-sm">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-primary-600" />
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold tracking-widest text-surface-400">Campaign Insights</p>
            <span className="text-sm font-bold text-surface-700">{format(new Date(), 'eeee, do MMMM')}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {selectedScheme && (
            <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-100 rounded-2xl">
              <SafeIcon icon={FiLayers} className="w-3.5 h-3.5 text-primary-500" />
              <span className="text-[10px] font-black text-primary-700 uppercase tracking-widest">{selectedScheme.name}</span>
            </div>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetch()} 
            className="rounded-xl border-surface-200 hover:border-primary-300 bg-white h-10 px-4"
            disabled={isFetching}
          >
            <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 mr-2 text-primary-600 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="text-xs font-bold uppercase tracking-wider">Refresh</span>
          </Button>
        </div>
      </div>

      <KpiSection data={dashboardData.kpi} />
      
      <ChartsSection 
        giftDist={dashboardData.gift_distribution}
        dailyTrend={dashboardData.daily_trend}
        cityStats={dashboardData.city_stats}
        funnel={dashboardData.funnel}
        liveActivity={dashboardData.live_activity}
      />
    </div>
  );
};

export default Dashboard;